import { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import CallKeyInfoItem from './CallKeyInfoItem';

export type TextInfoItem = {
    type: 'text';
    label: string;
    labelId: string;
    value: string | null | undefined;
};

export type QualityScoreInfoItem = {
    type: 'qualityScore';
    label: string;
    labelId: string;
    score: number;
};

export type SpeechProportionInfoItem = {
    type: 'speechProportion';
    label: string;
    labelId: string;
    proportion: number;
};

export type CallKeyInfoItemConfig = TextInfoItem | QualityScoreInfoItem | SpeechProportionInfoItem;

interface CallKeyInfoProps {
    duration?: string;
    endingReason?: string | null;
    agentSpeechProportion?: number | null;
    qualityScore?: number | null;
}

/**
 * Displays key information about a call.
 *
 * @param {CallKeyInfoProps} props
 * @returns {JSX.Element}
 */
const CallKeyInfo: React.FC<CallKeyInfoProps> = (props) => {
    const { duration, endingReason, agentSpeechProportion, qualityScore } = props;

    // Build an array of info items based on the available data
    const infoItems = useMemo(() => {
        const items: CallKeyInfoItemConfig[] = [];

        if (props.qualityScore != null) {
            items.push({
                type: 'qualityScore',
                label: 'Call score',
                labelId: 'quality-score-label',
                score: props.qualityScore,
            });
        }

        items.push(
            {
                type: 'text',
                label: 'Duration',
                labelId: 'call-duration-label',
                value: props.duration ?? 'N/A',
            },
            {
                type: 'text',
                label: 'Outcome',
                labelId: 'call-outcome-label',
                value: props.endingReason ?? 'N/A',
            }
        );

        if (props.agentSpeechProportion != null) {
            items.push({
                type: 'speechProportion',
                label: 'Talk time distribution',
                labelId: 'speech-proportion-label',
                proportion: props.agentSpeechProportion,
            });
        }

        return items;
    }, [duration, endingReason, agentSpeechProportion, qualityScore]);

    return (
        <Stack direction="column" alignItems="center" gap={0.5} mb={1}>
            {infoItems.map((item) => (
                <Box sx={{ width: '100%' }} key={item.labelId}>
                    <CallKeyInfoItem item={item} />
                </Box>
            ))}
        </Stack>
    );
};

export default CallKeyInfo;
